import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { savePresenceHeartbeat } from '../data/metaApi.js'
import { useUIStore } from '../store/uiStore.js'
import { useArchiveViewStore } from '../store/archiveViewStore.js'
import { PILLARS } from '../data/pillars.js'

/**
 * usePresenceHeartbeat — broadcasts this user's presence every 20 seconds.
 *
 * Uses the dedicated `POST /meta/presence` endpoint, which does a SERVER-SIDE
 * partial update: only meta.presence[userName] is touched, every other field
 * (actionItems, comments, weeklySummary, etc.) is preserved untouched.
 *
 * This eliminates a prior class of bugs where heartbeats could resurrect
 * deleted items or revert edits by POSTing a stale full-meta snapshot.
 * Heartbeats can no longer affect anything outside presence.
 *
 * We also optimistically update the local cache so our own presence shows
 * up instantly in the PresenceBar without waiting for the next 15s poll.
 */
export function usePresenceHeartbeat() {
  const currentUser = useUIStore((s) => s.currentUser)
  const activePillarIndex = useUIStore((s) => s.activePillar)
  const queryClient = useQueryClient()

  const activePillar = PILLARS[activePillarIndex]?.name || 'Unknown'

  useEffect(() => {
    if (!currentUser) return

    const beat = async () => {
      // While viewing a past week, don't broadcast presence or write
      // to the live meta — it'd show teammates as "on the dashboard"
      // when they're actually browsing frozen history, and it'd
      // needlessly touch live KV.
      if (useArchiveViewStore.getState().active) return

      const at = Date.now()
      // Optimistic: update local cache right away so our own presence
      // shows in the PresenceBar instantly.
      queryClient.setQueryData(['meta'], (old) => {
        if (!old) return old
        return {
          ...old,
          presence: {
            ...(old.presence || {}),
            [currentUser]: { at, pillar: activePillar },
          },
        }
      })
      // Fire-and-forget the partial update. Failures are non-critical
      // (presence is ephemeral; the next heartbeat will retry).
      savePresenceHeartbeat({ userName: currentUser, at, pillar: activePillar }).catch((err) => {
        console.warn('[presence-heartbeat] save failed', err)
      })
    }

    beat() // immediate
    const id = setInterval(beat, 20_000)
    return () => clearInterval(id)
  }, [currentUser, activePillar, queryClient])
}
